// src/pages/outsourcing_management/requisitions/RequisitionTable.tsx
//
// @tanstack/react-table v8 dashboard list for the internal Purchase
// Requisition feature (EE-207, Phase 2 #6/#7). Server-side pagination +
// sorting: the parent owns page/pageSize/sorting state and refetches via
// useRequisitionList, this component only renders what it's given
// (mirrors CustomerTable.tsx's manualPagination/manualSorting setup).
import { FunctionComponent } from "react";
import {
  LinearProgress,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import StatusUpdateIndicator from "components/Dashboard/StatusUpdateIndicator";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  RequisitionInquiryLinkType,
  RequisitionStatusEnum,
  RequisitionType,
} from "types/RequisitionTypes";
import {
  requisitionStatusLabel,
  REQUISITION_STATUS_COLORS,
} from "../constants/requisitionStatusColors";
import DashboardTableHeadCell, {
  DashboardTableRefreshCell,
} from "components/Dashboard/DashboardTableHeadCell";
import StatusToken from "components/Dashboard/StatusToken";
import { SKELETON_ROW_HEIGHT } from "components/Dashboard/dashboardTable";
import { reformatDate, reformatDateTime } from "util/DateUtil";
import { partSummary } from "./requisitionForm";

type PropsType = {
  requisitions: RequisitionType[];
  total: number;
  isFetching: boolean;
  page: number; // zero-based, matches MUI TablePagination
  pageSize: number;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  onRowClick?: (requisition: RequisitionType) => void;
  onRefresh?: () => void;
};

// A delivery date is a date; a created stamp is a moment (EE-238). Both
// dashboards follow the same split, and this matches RequisitionDetailDialog,
// which already showed created with its time.
const formatDateOrDash = (date: string | null) => (date ? reformatDate(date) : "-");

const formatTimestampOrDash = (date: string | null) =>
  date ? reformatDateTime(date) : "-";

const columnHelper = createColumnHelper<RequisitionType>();

// EE-253: the Inquiry drives everything between Approved and Ready to Use, so
// a requisition changes status without its requester doing anything. The
// indicator (StatusUpdateIndicator, shared with the inquiry board) says which
// status landed and when, and stays until somebody opens the requisition -
// the flag is global, so it clears for everyone at once
// (RequisitionDetailDialog fires the acknowledgement on open).
//
// Long enough to tell two comments apart at a glance, short enough that the
// column does not push the dates off screen.
const COMMENT_PREVIEW_CHARS = 40;

const columns = [
  // Status leads, matching the Inquiry table (EE-238).
  columnHelper.accessor("status", {
    id: "status",
    header: "Status",
    enableSorting: true,
    meta: { align: "center" as const },
    cell: (info) => {
      const status = info.getValue() as RequisitionStatusEnum;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.375rem",
          }}
        >
          <StatusToken
            label={requisitionStatusLabel(status)}
            colors={REQUISITION_STATUS_COLORS[status]}
          />
          <StatusUpdateIndicator
            pending={info.row.original.status_ack_pending}
            statusLabel={requisitionStatusLabel(status)}
            changedAt={info.row.original.status_changed_at}
          />
        </div>
      );
    },
  }),
  // EE-253 P2b: the status is the least-advanced line, so the counts say how
  // far the rest have got. Their own column, one count per line, so they
  // line up row to row whatever the status label or update icon before them.
  columnHelper.display({
    id: "progress",
    header: "Progress",
    cell: (info) => {
      const row = info.row.original;
      if (row.lines_total == null || row.status === RequisitionStatusEnum.DRAFT)
        return "-";
      return (
        <div
          style={{
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
          }}
          data-testid={`req-lines-${row.id}`}
        >
          <div>
            {row.lines_in_enquiry ?? 0}/{row.lines_total} in Inquiry
          </div>
          <div>
            {row.lines_ready ?? 0}/{row.lines_total} ready
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.requisition_no ?? `#${row.id}`, {
    id: "id",
    header: "Requisition No",
    enableSorting: true,
  }),
  // The Inquiry the requisition turned into. Newest first from the backend,
  // and normally only one - a resubmitted requisition can leave a closed one
  // behind, so the rest sit in the tooltip rather than pile up in the cell.
  columnHelper.display({
    id: "inquiry",
    header: "Inquiry No",
    cell: (info) => {
      const inquiries = info.row.original.inquiries ?? [];
      if (inquiries.length === 0) return "-";
      const label = (inquiry: RequisitionInquiryLinkType) =>
        inquiry.inquiry_no ?? `#${inquiry.id}`;
      // EE-253 P2b: a requisition's lines can sit in several
      // Inquiries at once, so every one is listed.
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {inquiries.map((inquiry) => (
            <span key={inquiry.id}>{label(inquiry)}</span>
          ))}
        </div>
      );
    },
  }),
  // Straight after the ids: the job is what people scan this board for.
  // Supplier and Requester follow it, Type and Purpose sit behind them.
  columnHelper.display({
    id: "so_part",
    header: "SO / Quote / Part",
    // A requisition names an order or a quote, never both. This read the
    // order pair only, so every quote-side requisition showed "-" while the
    // reference it holds was sitting in the payload unused.
    cell: (info) => {
      const row = info.row.original;
      const reference = row.sales_order_no || row.quote_name || null;
      // Every part the lines point at, not only the header's: a requisition
      // covers several parts of one order or quote.
      const part = partSummary(
        row.items,
        row.order_item_name || row.quote_item_name || null
      );
      if (!reference && !part) return "-";
      return (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {reference && (
            <span style={{ fontSize: "0.85rem" }}>{reference}</span>
          )}
          {part && (
            <span style={{ fontSize: "0.75rem", opacity: 0.75 }}>{part}</span>
          )}
        </div>
      );
    },
  }),
  columnHelper.accessor((row) => row.supplier_name ?? "-", {
    id: "supplier_id",
    header: "Supplier",
    enableSorting: true,
  }),
  columnHelper.accessor((row) => row.requester_name ?? row.requester_email ?? "-", {
    id: "requester_id",
    header: "Requester",
    enableSorting: true,
  }),
  columnHelper.accessor((row) => row.type_label ?? row.type_code ?? "-", {
    id: "type",
    header: "Type",
    enableSorting: false,
  }),
  columnHelper.accessor((row) => row.purpose_label ?? row.purpose_code ?? "-", {
    id: "purpose",
    header: "Purpose",
    enableSorting: false,
  }),
  // Dates run oldest first: Created, then Expected, then Planned Delivery.
  columnHelper.accessor((row) => row.created, {
    id: "created",
    header: "Created",
    enableSorting: true,
    cell: (info) => formatTimestampOrDash(info.getValue()),
    meta: { align: "right" as const },
  }),
  // `meta.align` is forwarded to both the header cell and the body cell
  // (EE-238) - without it, date columns here rendered left-aligned while the
  // Inquiry table right-aligns its Deadline column.
  columnHelper.accessor((row) => row.required_by_date, {
    id: "required_by_date",
    header: "Expected Delivery",
    enableSorting: true,
    cell: (info) => formatDateOrDash(info.getValue()),
    meta: { align: "right" as const },
  }),
  columnHelper.accessor((row) => row.estimated_delivery_date, {
    id: "estimated_delivery_date",
    header: "Planned Delivery",
    enableSorting: true,
    cell: (info) => formatDateOrDash(info.getValue()),
    meta: { align: "right" as const },
  }),
  columnHelper.accessor((row) => row.comments, {
    id: "comments",
    header: "Comments",
    enableSorting: false,
    // Truncated in the row with the full text on hover. Comments are the one
    // field that says why a requisition looks the way it does, and having to
    // open each row to read one made the board unscannable.
    cell: (info) => {
      const comment = (info.getValue() ?? "").trim();
      if (!comment) return "-";
      const shortened =
        comment.length > COMMENT_PREVIEW_CHARS
          ? `${comment.slice(0, COMMENT_PREVIEW_CHARS)}...`
          : comment;
      return (
        <Tooltip title={comment}>
          <span>{shortened}</span>
        </Tooltip>
      );
    },
  }),
  // Last, matching the Inquiry board: it is the widest column and prose, so
  // anything after it loses its alignment. The note itself belongs to the
  // Inquiry - a requisition has no notes of its own - and is shown here
  // because this board is where somebody asks what is happening with a job.
  columnHelper.accessor((row) => row.latest_note, {
    id: "latest_note",
    header: "Latest note",
    enableSorting: false,
    cell: (info) => {
      const note = (info.getValue() ?? "").trim();
      if (!note) return "-";
      const shortened =
        note.length > COMMENT_PREVIEW_CHARS
          ? `${note.slice(0, COMMENT_PREVIEW_CHARS)}...`
          : note;
      return (
        <Tooltip title={note}>
          <span>{shortened}</span>
        </Tooltip>
      );
    },
  }),
];

type ColumnAlign = "left" | "center" | "right";

const alignOf = (meta: unknown): ColumnAlign =>
  (meta as { align?: ColumnAlign } | undefined)?.align ?? "left";

const RequisitionTable: FunctionComponent<PropsType> = ({
  requisitions,
  total,
  isFetching,
  page,
  pageSize,
  sorting,
  onSortingChange,
  onPageChange,
  onPageSizeChange,
  onRowClick,
  onRefresh,
}) => {
  const table = useReactTable({
    data: requisitions,
    columns,
    state: { sorting },
    onSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount: Math.max(1, Math.ceil(total / pageSize)),
  });

  // The refresh control sits in its own trailing header cell (matching the
  // Inquiry table), so full-width rows have to span it too.
  const colSpan = columns.length + (onRefresh ? 1 : 0);

  return (
    <Paper sx={{ width: "100%" }}>
      {isFetching ? <LinearProgress /> : null}

      <TableContainer>
        <Table>
          <TableHead sx={{ background: "var(--header)" }}>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const activeSortEntry = sorting.find((s) => s.id === header.column.id);
                  return (
                    <DashboardTableHeadCell
                      key={header.id}
                      align={alignOf(header.column.columnDef.meta)}
                      onSort={
                        canSort
                          ? () => header.column.toggleSorting(undefined, false)
                          : undefined
                      }
                      sortActive={!!activeSortEntry}
                      sortDirection={activeSortEntry?.desc === false ? "asc" : "desc"}
                      disabled={isFetching}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </DashboardTableHeadCell>
                  );
                })}
                {onRefresh && (
                  <DashboardTableRefreshCell
                    onRefresh={onRefresh}
                    disabled={isFetching}
                  />
                )}
              </TableRow>
            ))}
          </TableHead>
          {isFetching ? (
            <TableBody>
              {Array.from({ length: pageSize }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell colSpan={colSpan}>
                    <Skeleton variant="rectangular" height={SKELETON_ROW_HEIGHT} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          ) : requisitions.length > 0 ? (
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.original.id}
                  hover={!!onRowClick}
                  onClick={() => onRowClick?.(row.original)}
                  sx={{ cursor: onRowClick ? "pointer" : "default" }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      align={alignOf(cell.column.columnDef.meta)}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                  {onRefresh && <TableCell />}
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <TableBody>
              <TableRow>
                <TableCell colSpan={colSpan} style={{ textAlign: "center" }}>
                  No requisitions found
                </TableCell>
              </TableRow>
            </TableBody>
          )}
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={total}
        rowsPerPage={pageSize}
        page={page}
        onPageChange={(_: unknown, newPage: number) => onPageChange(newPage)}
        onRowsPerPageChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onPageSizeChange(+e.target.value)
        }
      />
    </Paper>
  );
};

export default RequisitionTable;
